import React, { useState } from 'react';
import {
  Box, Typography, Button, Stack, TextField, IconButton,
} from '@mui/material';
import { Plus, Trash2, GripVertical } from 'lucide-react';

const navy = '#1e3a5f';
const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const textSnippet = (text, max = 50) => {
  const plain = (text || '').replace(/<[^>]*>/g, '').trim();
  return plain.length > max ? plain.slice(0, max) + '…' : plain;
};

/**
 * MoadDrashotStep
 * הגדרת דרשות לימי מועד — כל דרשה משויכת ליום ספציפי מתוך ימי המועד שהוגדרו
 *
 * Props:
 *   formData  { days: [...], moadDrashot: [{ dayIndex, text }] }
 *   onChange  (partial) => void
 */
const MoadDrashotStep = ({ formData, onChange }) => {
  const days = formData?.days || [];
  const drashot = formData?.moadDrashot || [];
  const [editingIndex, setEditingIndex] = useState(null);
  const [draftText, setDraftText] = useState('');

  const updateDrashot = (updated) => onChange({ moadDrashot: updated });

  const removeDrasha = (dayIndex) =>
    updateDrashot(drashot.filter((d) => d.dayIndex !== dayIndex));

  const startEditing = (dayIndex) => {
    const existing = drashot.find((d) => d.dayIndex === dayIndex);
    setEditingIndex(dayIndex);
    setDraftText(existing?.text || '');
  };

  const saveDraft = () => {
    if (editingIndex === null) return;
    const text = draftText.trim();
    if (!text) {
      // אם הטקסט ריק — מחק את הדרשה
      removeDrasha(editingIndex);
    } else {
      const exists = drashot.some((d) => d.dayIndex === editingIndex);
      if (exists) {
        updateDrashot(drashot.map((d) => d.dayIndex === editingIndex ? { ...d, text } : d));
      } else {
        updateDrashot([...drashot, { dayIndex: editingIndex, text }]);
      }
    }
    setEditingIndex(null);
    setDraftText('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setDraftText('');
  };

  if (days.length === 0) {
    return (
      <Box dir="rtl">
        <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
          דרשות המועד
        </Typography>
        <Box sx={{ textAlign: 'center', py: 4, px: 2, bgcolor: '#fef3c7', borderRadius: 2, border: '1px solid #fde68a' }}>
          <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>📅</Typography>
          <Typography sx={{ fontWeight: 600, color: '#92400e', mb: 0.5 }}>קודם הגדר את ימי המועד</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#b45309' }}>חזור לשלב "ימי המועד" והוסף לפחות יום אחד</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        דרשות המועד
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.5 }}>
        הוסף דרשה לכל אחד מימי המועד. הדרשה תוצג אוטומטית בתצוגת הרכיב — אם אין דרשה ליום מסוים, לא יוצג כלום.
      </Typography>

      <Stack spacing={1.5}>
        {days.map((day, dayIdx) => {
          const existing = drashot.find((d) => d.dayIndex === dayIdx);
          const isEditing = editingIndex === dayIdx;
          const dayColor = day.color || '#166534';

          return (
            <Box
              key={dayIdx}
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                overflow: 'hidden',
                borderRight: `3px solid ${dayColor}`,
              }}
            >
              {/* Day header */}
              <Box
                sx={{
                  px: 2, py: 1.25,
                  display: 'flex', alignItems: 'center', gap: 1,
                  bgcolor: '#fafafa',
                  borderBottom: (existing || isEditing) ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <GripVertical size={14} color="#d1d5db" style={{ flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827', flex: 1 }}>
                  {day.name || `יום ${dayIdx + 1}`}
                </Typography>
                {existing && !isEditing && (
                  <IconButton
                    size="small"
                    onClick={() => removeDrasha(dayIdx)}
                    sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' } }}
                  >
                    <Trash2 size={14} />
                  </IconButton>
                )}
              </Box>

              {/* Content area */}
              {isEditing ? (
                <Box sx={{ p: 2, bgcolor: '#f0f9ff' }}>
                  <TextField
                    size="small"
                    label="תוכן הדרשה"
                    placeholder='לדוגמה: הרב ישראל ישראלי ידרוש בנושא...'
                    value={draftText}
                    onChange={(e) => setDraftText(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    autoFocus
                    sx={{ ...inputSx, '& .MuiInputLabel-root': { fontSize: '0.82rem' }, bgcolor: 'white' }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                    <Button
                      onClick={cancelEdit}
                      sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      ביטול
                    </Button>
                    <Button
                      variant="contained"
                      onClick={saveDraft}
                      sx={{
                        bgcolor: navy, color: 'white', textTransform: 'none', fontSize: '0.8rem',
                        boxShadow: 'none', borderRadius: '8px',
                        '&:hover': { bgcolor: '#152844', boxShadow: 'none' },
                      }}
                    >
                      {existing ? 'עדכן' : 'שמור'}
                    </Button>
                  </Stack>
                </Box>
              ) : existing ? (
                <Box
                  sx={{
                    px: 2, py: 1.25, cursor: 'pointer',
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}
                  onClick={() => startEditing(dayIdx)}
                >
                  <Typography sx={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {textSnippet(existing.text, 100)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af', mt: 0.5 }}>
                    לחץ לעריכה
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ px: 2, py: 1 }}>
                  <Button
                    startIcon={<Plus size={13} />}
                    onClick={() => startEditing(dayIdx)}
                    sx={{
                      color: '#9ca3af', textTransform: 'none',
                      fontSize: '0.78rem', py: 0.25,
                      '&:hover': { color: navy, bgcolor: '#f0f4fa' },
                    }}
                  >
                    הוסף דרשה
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default MoadDrashotStep;
